import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import client, { resolveImageUrl } from "@/lib/api";
import { useAuth, startLogin } from "@/lib/auth";
import { MessageSquarePlus, MessageCircle, Filter, Shield, Sparkles, LogIn, Megaphone, Globe2, MessagesSquare, Wrench, Lightbulb, Languages, Loader2 } from "lucide-react";
import ForumEditorToolbar from "@/components/ForumEditorToolbar";
import ForumBody from "@/components/ForumBody";

const GROUP_ICONS = {
  "official": Megaphone,
  "world-stage": Globe2,
  "general-discussion": MessagesSquare,
  "dev-wiki": Wrench,
};

function formatDate(iso) {
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  } catch {
    return "";
  }
}

export default function Forum() {
  const { t } = useTranslation();
  const [groups, setGroups] = useState([]);
  const [posts, setPosts] = useState([]);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const activeCategory = params.get("category") || "";

  useEffect(() => {
    client.get("/forum/categories").then(r => setGroups(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const q = activeCategory ? { params: { category: activeCategory } } : {};
    client.get("/forum/posts", q).then(r => setPosts(r.data)).catch(() => setPosts([]));
  }, [activeCategory]);

  const setCat = (slug) => {
    const sp = new URLSearchParams(params);
    if (slug) sp.set("category", slug); else sp.delete("category");
    setParams(sp);
  };

  const allChannels = useMemo(
    () => groups.flatMap(g => g.channels.map(c => ({ ...c, group: g.slug, group_name: g.name }))),
    [groups]
  );

  const catName = useMemo(() => {
    if (!activeCategory) return t("forum.filterAll");
    const c = allChannels.find(c => c.slug === activeCategory);
    return c?.name || activeCategory;
  }, [activeCategory, allChannels]);

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 md:py-12" data-testid="forum-page">
      <div className="flex items-start justify-between gap-4 mb-6 md:mb-8 flex-wrap">
        <div>
          <span className="font-mono text-[10px] md:text-xs tracking-[0.25em] md:tracking-[0.3em] text-[var(--t-blue-glow)] uppercase">{t("forum.categoryEyebrow")}</span>
          <h1 className="font-display text-3xl md:text-5xl mt-2 text-[var(--t-text)] uppercase leading-tight">{t("forum.title")}</h1>
          <p className="text-sm md:text-base text-[var(--t-text-dim)] mt-3 max-w-2xl">
            The war room. Trade strategy, file bug reports, swap lore. The best threads get promoted into the official codex by admins.
          </p>
        </div>
        {user ? (
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/contribute" className="btn-ghost !text-xs" data-testid="forum-contribute-link">
              <Lightbulb className="w-4 h-4" /> Contribute a Tip
            </Link>
            <button
              onClick={() => navigate("/forum/new" + (activeCategory ? `?category=${activeCategory}` : ""))}
              className="btn-primary !text-xs"
              data-testid="new-post-btn"
            >
              <MessageSquarePlus className="w-4 h-4" /> New Transmission
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/contribute" className="btn-ghost !text-xs" data-testid="forum-contribute-link">
              <Lightbulb className="w-4 h-4" /> Contribute a Tip
            </Link>
            <button onClick={startLogin} className="btn-ghost !text-xs" data-testid="forum-signin-btn">
              <LogIn className="w-4 h-4" /> Sign in to Post
            </button>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-[220px_1fr] gap-6 md:gap-8">
        {/* Sidebar */}
        <aside className="space-y-1.5" data-testid="forum-sidebar">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-3.5 h-3.5 text-[var(--t-blue-glow)]" />
            <span className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase">Channels</span>
          </div>
          <button
            onClick={() => setCat("")}
            className={`w-full text-left px-3 py-2 font-display text-xs tracking-[0.14em] uppercase border-l-2 transition ${
              !activeCategory
                ? "border-[var(--t-blue)] text-[var(--t-cyan)] bg-[rgba(58,160,255,0.08)]"
                : "border-transparent text-[var(--t-steel)] hover:text-[var(--t-blue-glow)] hover:bg-[rgba(58,160,255,0.05)]"
            }`}
            data-testid="cat-all"
          >
            All Channels
          </button>
          {groups.map(g => {
            const Icon = GROUP_ICONS[g.slug];
            return (
              <div key={g.slug} className="pt-2" data-testid={`forum-group-${g.slug}`}>
                <div className="flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] tracking-[0.22em] text-[var(--t-blue-glow)]/80 uppercase">
                  {Icon && <Icon className="w-3 h-3" />}
                  <span>{g.name}</span>
                </div>
                {g.channels.map(c => (
                  <button
                    key={c.slug}
                    onClick={() => setCat(c.slug)}
                    title={c.description}
                    className={`w-full text-left px-3 py-1.5 font-display text-[11px] tracking-[0.12em] uppercase border-l-2 transition ${
                      activeCategory === c.slug
                        ? "border-[var(--t-blue)] text-[var(--t-cyan)] bg-[rgba(58,160,255,0.08)]"
                        : "border-transparent text-[var(--t-steel)] hover:text-[var(--t-blue-glow)] hover:bg-[rgba(58,160,255,0.05)]"
                    }`}
                    data-testid={`cat-${c.slug}`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            );
          })}
        </aside>

        {/* Posts list */}
        <section data-testid="forum-posts">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg md:text-xl text-[var(--t-cyan)] uppercase tracking-[0.14em]">{catName}</h2>
            <span className="font-mono text-[10px] tracking-[0.2em] text-[var(--t-text-muted)] uppercase">{posts.length} {t("forum.replies")}</span>
          </div>

          {posts.length === 0 && (
            <div className="panel p-10 text-center" data-testid="forum-empty">
              <div className="font-mono text-xs tracking-[0.25em] text-[var(--t-text-muted)] uppercase mb-4">// silence on the wire</div>
              <p className="text-sm text-[var(--t-text-dim)] mb-5">No threads here yet. Be the first to transmit.</p>
              {user ? (
                <button onClick={() => navigate("/forum/new" + (activeCategory ? `?category=${activeCategory}` : ""))} className="btn-primary !text-xs">
                  <MessageSquarePlus className="w-4 h-4" /> Start a Thread
                </button>
              ) : (
                <button onClick={startLogin} className="btn-ghost !text-xs">
                  <LogIn className="w-4 h-4" /> Sign in to Post
                </button>
              )}
            </div>
          )}

          <div className="space-y-3">
            {posts.map(p => {
              const cat = allChannels.find(c => c.slug === p.category);
              return (
                <Link
                  key={p.id}
                  to={`/forum/${p.id}`}
                  className="panel panel-glow block p-4 md:p-5 hover:border-[var(--t-blue)] transition"
                  data-testid={`forum-post-${p.id}`}
                >
                  <div className="flex items-start gap-3 md:gap-4">
                    {p.author_picture ? (
                      <img src={p.author_picture} alt="" className="w-9 h-9 rounded-full border border-[var(--t-border-glow)] flex-shrink-0" />
                    ) : (
                      <div className="w-9 h-9 rounded-full border border-[var(--t-border-glow)] bg-[rgba(58,160,255,0.08)] flex items-center justify-center text-[var(--t-blue-glow)] font-mono text-xs flex-shrink-0">
                        {(p.author_name || "C").slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="font-mono text-[10px] tracking-[0.22em] text-[var(--t-blue-glow)] uppercase">{cat?.name || p.category}</span>
                        {p.promoted_slug && (
                          <span className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.18em] text-[var(--t-green)] uppercase px-1.5 py-0.5 border border-[var(--t-green)]/40">
                            <Sparkles className="w-3 h-3" /> In Codex
                          </span>
                        )}
                      </div>
                      <h3 className="font-display text-base md:text-lg text-[var(--t-cyan)] uppercase leading-tight mb-1.5">{p.title}</h3>
                      <div className="flex items-center gap-3 flex-wrap font-mono text-[10px] text-[var(--t-text-muted)] tracking-[0.14em]">
                        <span>by {p.author_name}</span>
                        <span>·</span>
                        <span>{formatDate(p.created_at)}</span>
                        <span>·</span>
                        <span className="inline-flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {p.reply_count || 0}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}

export function ForumPostDetail() {
  // Lazy-load to keep this file self-contained for App.js
  return <ForumPostInner />;
}

function ForumPostInner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const replyRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [promoting, setPromoting] = useState(false);

  // Live translation state — keyed by language so a switch re-uses cached responses.
  const [translation, setTranslation] = useState(null);     // {post:{title,body}, replies:{id:body}}
  const [translating, setTranslating] = useState(false);
  const [translatedLang, setTranslatedLang] = useState(null);

  const load = () => client.get(`/forum/posts/${id}`).then(r => setData(r.data)).catch(() => setData({ post: null, replies: [] }));
  useEffect(() => { load(); }, [id]);

  // When the global language changes, hide any stale translation in the wrong language.
  useEffect(() => {
    if (translatedLang && translatedLang !== i18n.language) {
      setTranslation(null);
      setTranslatedLang(null);
    }
  }, [i18n.language, translatedLang]);

  const translatePost = async () => {
    if (translating) return;
    setTranslating(true);
    try {
      const r = await client.get(`/forum/posts/${id}/translate`, { params: { language: i18n.language } });
      const replyMap = {};
      (r.data.replies || []).forEach(rr => { replyMap[rr.id] = rr.body; });
      setTranslation({ post: r.data.post, replies: replyMap });
      setTranslatedLang(i18n.language);
    } catch (e) {
      alert(e?.response?.data?.detail || "Translation failed.");
    } finally {
      setTranslating(false);
    }
  };

  const showOriginal = () => {
    setTranslation(null);
    setTranslatedLang(null);
  };

  if (!data) {
    return <div className="text-center py-32 font-mono text-xs tracking-[0.3em] text-[var(--t-blue-glow)] uppercase pulse-glow" data-testid="forum-post-loading">// loading transmission...</div>;
  }
  if (!data.post) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="forum-post-not-found">
        <h1 className="font-display text-3xl text-[var(--t-red)] uppercase mb-4">Transmission Lost</h1>
        <p className="text-[var(--t-text-dim)] mb-6">This thread was deleted or never existed.</p>
        <Link to="/forum" className="btn-ghost">← Back to Forum</Link>
      </div>
    );
  }
  const { post, replies } = data;

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { startLogin(); return; }
    if (reply.trim().length < 2) { setError("Reply too short."); return; }
    setBusy(true); setError("");
    try {
      await client.post(`/forum/posts/${id}/replies`, { body: reply });
      setReply("");
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to send.");
    } finally { setBusy(false); }
  };

  const deletePost = async () => {
    if (!window.confirm("Delete this thread and all replies?")) return;
    await client.delete(`/forum/posts/${id}`);
    navigate("/forum");
  };

  const deleteReply = async (rid) => {
    if (!window.confirm("Delete this reply?")) return;
    await client.delete(`/forum/posts/${id}/replies/${rid}`);
    load();
  };

  const promote = async () => {
    if (!window.confirm("Promote this thread to the official wiki?")) return;
    setPromoting(true);
    try {
      const r = await client.post(`/forum/posts/${id}/promote`);
      alert(`Promoted to /wiki/${r.data.slug}`);
      load();
    } catch (err) {
      alert(err?.response?.data?.detail || "Promotion failed.");
    } finally { setPromoting(false); }
  };

  const canDeletePost = user && (user.is_admin || user.user_id === post.author_id);

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12" data-testid="forum-post-detail">
      <Link to={`/forum?category=${post.category}`} className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase hover:text-[var(--t-cyan)]" data-testid="forum-back-link">← back to /{post.category}</Link>

      <article className="panel panel-glow mt-4 overflow-hidden">
        {post.author_banner && (
          <div className="relative h-28 md:h-36 w-full overflow-hidden" data-testid="forum-post-banner">
            <img src={resolveImageUrl(post.author_banner)} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#050912]/85 pointer-events-none" />
          </div>
        )}
        <div className={`p-5 md:p-7 ${post.author_banner ? "-mt-10 md:-mt-12 relative" : ""}`}>
        <div className="flex items-start gap-3 md:gap-4 mb-4">
          {post.author_picture ? (
            <img src={resolveImageUrl(post.author_picture)} alt="" className="w-12 h-12 rounded-full border border-[var(--t-border-glow)] flex-shrink-0 bg-[#050912] object-cover" />
          ) : (
            <div className="w-12 h-12 rounded-full border border-[var(--t-border-glow)] bg-[rgba(58,160,255,0.08)] flex items-center justify-center text-[var(--t-blue-glow)] font-mono flex-shrink-0">
              {(post.author_name || "C").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h1 className="font-display text-2xl md:text-3xl text-[var(--t-text)] uppercase leading-tight mb-2">{translation?.post?.title || post.title}</h1>
            <div className="flex items-center gap-3 flex-wrap font-mono text-[10px] text-[var(--t-text-muted)] tracking-[0.18em] uppercase">
              <span className="text-[var(--t-cyan)]">{post.author_name}</span>
              <span>·</span>
              <span>{formatDate(post.created_at)}</span>
              {post.promoted_slug && (
                <Link to={`/wiki/${post.promoted_slug}`} className="inline-flex items-center gap-1 text-[var(--t-green)] hover:underline" data-testid="forum-promoted-link">
                  <Sparkles className="w-3 h-3" /> in codex
                </Link>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2 flex-shrink-0">
            {i18n.language !== "en" && !translation && (
              <button onClick={translatePost} disabled={translating} className="btn-ghost !py-1.5 !px-3 !text-[10px] !border-[var(--t-blue)] !text-[var(--t-cyan)]" data-testid="translate-post-btn">
                {translating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Languages className="w-3 h-3" />}
                {translating ? "..." : "Translate"}
              </button>
            )}
            {translation && (
              <button onClick={showOriginal} className="btn-ghost !py-1.5 !px-3 !text-[10px]" data-testid="show-original-btn">
                <Languages className="w-3 h-3" /> Original
              </button>
            )}
            {user?.is_admin && !post.promoted_slug && (
              <button onClick={promote} disabled={promoting} className="btn-primary !py-1.5 !px-3 !text-[10px]" data-testid="promote-btn">
                <Sparkles className="w-3 h-3" /> {promoting ? "..." : "Promote"}
              </button>
            )}
            {canDeletePost && (
              <button onClick={deletePost} className="btn-ghost !py-1.5 !px-3 !text-[10px] !border-[var(--t-red)] !text-[var(--t-red)]" data-testid="delete-post-btn">
                Delete
              </button>
            )}
          </div>
        </div>
        {translation && (
          <div className="mb-3 px-3 py-2 border border-[var(--t-blue)]/40 bg-[rgba(58,160,255,0.06)] text-[10px] md:text-xs font-mono tracking-[0.14em] text-[var(--t-blue-glow)] uppercase inline-flex items-center gap-2" data-testid="translation-banner">
            <Languages className="w-3 h-3" /> AI translation · {translatedLang?.toUpperCase()}
          </div>
        )}
        <ForumBody text={translation?.post?.body || post.body} className="text-sm md:text-base" testId="forum-post-body" />
        </div>
      </article>

      <section className="mt-8" data-testid="forum-replies">
        <h2 className="font-display text-sm tracking-[0.2em] text-[var(--t-blue-glow)] uppercase mb-4">// {replies.length} {replies.length === 1 ? "Reply" : "Replies"}</h2>
        <div className="space-y-3">
          {replies.map(r => {
            const canDel = user && (user.is_admin || user.user_id === r.author_id);
            return (
              <div key={r.id} className="panel p-4 md:p-5" data-testid={`forum-reply-${r.id}`}>
                <div className="flex items-start gap-3">
                  {r.author_picture ? (
                    <img src={resolveImageUrl(r.author_picture)} alt="" className="w-8 h-8 rounded-full border border-[var(--t-border-glow)] flex-shrink-0 object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full border border-[var(--t-border-glow)] bg-[rgba(58,160,255,0.08)] flex items-center justify-center text-[var(--t-blue-glow)] font-mono text-xs flex-shrink-0">
                      {(r.author_name || "C").slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1.5 font-mono text-[10px] text-[var(--t-text-muted)] tracking-[0.16em] uppercase">
                      <span className="text-[var(--t-cyan)]">{r.author_name}</span>
                      <span>·</span>
                      <span>{formatDate(r.created_at)}</span>
                    </div>
                    <ForumBody text={translation?.replies?.[r.id] || r.body} className="text-sm" />
                  </div>
                  {canDel && (
                    <button onClick={() => deleteReply(r.id)} className="btn-ghost !py-1 !px-2 !text-[10px] !border-[var(--t-red)] !text-[var(--t-red)]" data-testid={`delete-reply-${r.id}`}>
                      Delete
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <form onSubmit={submit} className="panel p-5 mt-6" data-testid="forum-reply-form">
          <label className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase block mb-2">// Transmit Reply</label>
          {user ? (
            <>
              <ForumEditorToolbar
                textareaRef={replyRef}
                value={reply}
                onChange={setReply}
                testidPrefix="reply"
              />
              <textarea
                ref={replyRef}
                className="field"
                rows={4}
                value={reply}
                onChange={e => setReply(e.target.value)}
                placeholder="Share your intel..."
                data-testid="forum-reply-input"
              />
              {error && <div className="text-sm text-[var(--t-red)] font-mono mt-2" data-testid="forum-reply-error">{error}</div>}
              <div className="flex justify-between items-center mt-3">
                <span className="font-mono text-[10px] text-[var(--t-text-muted)] tracking-[0.18em] uppercase">signed in as {user.name || user.email}</span>
                <button type="submit" disabled={busy} className="btn-primary !text-xs" data-testid="forum-reply-submit">
                  {busy ? "Sending..." : "Send"}
                </button>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <span className="text-sm text-[var(--t-text-dim)]">{t("forum.signInToReply")}</span>
              <button type="button" onClick={startLogin} className="btn-ghost !text-xs" data-testid="forum-reply-signin">
                <LogIn className="w-4 h-4" /> Sign in
              </button>
            </div>
          )}
        </form>

        {user?.is_admin && (
          <div className="mt-6 panel p-4 border-[var(--t-border-glow)] flex items-start gap-3" data-testid="forum-admin-note">
            <Shield className="w-4 h-4 text-[var(--t-cyan)] mt-0.5 flex-shrink-0" />
            <p className="text-xs text-[var(--t-text-dim)]">
              <strong className="text-[var(--t-cyan)]">Admin:</strong> Use <em>Promote</em> to copy this thread into the official codex as a community-credited wiki page.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
